/**
 * The focus indicator, for anything that is not one of the `ui/` primitives.
 *
 * The primitives ring at `ring-3` over `ring-ring`; feature code had grown
 * two other answers — forty call sites at `ring-2` over solid `ring-ring`, and
 * two at `ring-1`, both of which were the label chip's own buttons. The smallest
 * control in the app had the faintest indicator in the app.
 *
 * One value, matching the primitives, so a focus ring means the same thing
 * wherever the keyboard lands.
 */
export const FOCUS_RING =
  'focus-visible:ring-3 focus-visible:ring-ring focus-visible:outline-none';

/**
 * The box the board header's pills stand in — shape, height and type size.
 *
 * Separated from `HEADER_PILL` because a *label* chip in a filter row has to
 * occupy exactly this box while wearing none of its border or hover behaviour:
 * a chip is a filled pill in its label's own slot colour, and the fill is what
 * keeps labels from being read as state.
 *
 * The height is explicit rather than derived from padding. `py-1 text-xs` gave
 * the state pills a 26px box while `LabelChip`'s `h-5` gave the label pills a
 * 20px one, so the two families that `HEADER_PILL` exists to unify (FR-023) sat
 * six pixels and one type step apart on the same row. A stated height is the
 * only version of this that two different paddings cannot drift out of.
 */
export const HEADER_PILL_BOX =
  'inline-flex h-7 w-fit shrink-0 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium whitespace-nowrap';

/**
 * The board header's pill, shared by the two families that sit in its row.
 *
 * `BoardActivitySummary`'s three state pills and `LabelFilterControl`'s trigger
 * are required to read as one family (FR-023), and they sat in different feature
 * packages with the class string copied between them — which is a rule enforced
 * by nobody. One constant makes it structural.
 *
 * What deliberately stays *different* is the leading dot: state pills carry one,
 * label pills never do. That is the discriminator in a row that mixes them, so
 * it belongs to each caller rather than in here.
 */
/*
 * `touch-target` gives the pill a 44px floor under a coarse pointer (spec 185):
 * these are the filters a phone reaches for, and at 28px they were the most
 * common small target on the Tasks page and the board. The label chips that
 * borrow `HEADER_PILL_BOX` are not controls, so the floor sits here, not there.
 */
export const HEADER_PILL = `${HEADER_PILL_BOX} touch-target border transition-colors ${FOCUS_RING}`;

/** A pill standing for a filter that is currently on. */
export const HEADER_PILL_ACTIVE =
  'border-foreground bg-foreground text-background';

/** A pill offering a filter that is not currently on. */
export const HEADER_PILL_IDLE =
  'bg-card border-border text-foreground hover:border-foreground/40';
